import * as React from 'react';
import { Button, Stack } from '@treatwell/ui';
import { Chevron, Direction } from 'js/components/Icons/Chevron';
import { Brand } from 'js/model/rainbow/venue/Brand';
import { BrandFilterItem } from './BrandFilterItem';
import * as styles from './BrandFilter.module.css';

type AllBrands = {
  id: AllBrandsId;
  name: string;
};

export type Brands = [AllBrands, ...Array<Brand>];
export type AllBrandsId = typeof ALL_BRANDS;

interface Props {
  brands: Brands;
  selectedBrandIds: (number | AllBrandsId)[];
  initiallyExpanded?: boolean;
  onChange: (aggregationId: number | AllBrandsId) => void;
  cms?: {
    filterVisibilityToggleLabelExpanded: string;
    filterVisibilityToggleLabelCollapsed: string;
  };
  disableCollapse?: boolean;
}

export const ALL_BRANDS = 'allBrands';

export function BrandFilter(props: Props): React.ReactElement {
  const {
    brands,
    selectedBrandIds,
    initiallyExpanded = false,
    onChange,
    cms,
  } = props;

  const MAX_INITIALLY_SHOWN = 10;

  const [showAllBrands, setShowAllBrands] =
    React.useState<boolean>(initiallyExpanded);

  function handleBrandClick(brand: AllBrands | Brand): void {
    onChange(brand.id);
  }

  function handleShowAllClick(): void {
    setShowAllBrands((prevShowAll) => {
      const showAll = !prevShowAll;
      return showAll;
    });
  }

  function renderBrandFilter(brand: AllBrands | Brand): React.ReactElement {
    return (
      <BrandFilterItem
        key={brand.id}
        value={brand.id}
        label={brand.name}
        isSelected={selectedBrandIds.includes(brand.id)}
        onClick={() => handleBrandClick(brand)}
      />
    );
  }

  let brandsToShow = brands.slice(0, MAX_INITIALLY_SHOWN);

  if (brands.length <= MAX_INITIALLY_SHOWN || showAllBrands) {
    brandsToShow = brands;
  }

  return (
    <Stack as="ul" space="sm" data-cy="BrandFilter">
      {brandsToShow.map((brand) => (
        <li key={brand.name}>{renderBrandFilter(brand)}</li>
      ))}

      {brands.length >= MAX_INITIALLY_SHOWN && !props.disableCollapse && (
        <div className={styles.filterVisibilityToggleContainer}>
          <Button
            size="sm"
            onClick={handleShowAllClick}
            iconPosition="right"
            icon={
              <Chevron
                direction={showAllBrands ? Direction.Up : Direction.Down}
              />
            }
          >
            {showAllBrands
              ? cms?.filterVisibilityToggleLabelExpanded
              : cms?.filterVisibilityToggleLabelCollapsed}
          </Button>
        </div>
      )}
    </Stack>
  );
}
